import { BehaviorSubject, combineLatest, merge, Subject, timer } from 'rxjs';
import { LayerStyle, MapboxLayerType } from './layer.type';
import { LayerGroup } from './layer-group.model';
import { MapSettingsService } from '../components/map-settings/map-settings-service';
import { distinctUntilChanged, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { FlagService } from '~app/shared/flag/flag.service';

@Injectable()
export class BaseLayer implements OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();

  public readonly id: string;
  public readonly name: string;
  protected style?: { layerType?: MapboxLayerType; } & LayerStyle | { layerType?: 'html-marker'; sourceType?: 'geojson'; } & LayerStyle;

  protected mapRef: mapboxgl.Map;

  private readonly isVisibleBS = new BehaviorSubject({
    layerVisible: false
  });
  /** represents the visibility selection made by the user, and is persisted across reloads */
  public readonly isVisible$ = this.isVisibleBS.asObservable();

  private readonly shouldRenderBS = new BehaviorSubject(false); // see constructor
  /** determines if the layer should currently be rendered on the map, regardless of the value of `isVisible$` */
  public readonly shouldRender$ = this.shouldRenderBS.asObservable();

  protected layerGroup: LayerGroup;

  private readonly isSelectedBS = new BehaviorSubject(false);
  public readonly isSelected$ = this.isSelectedBS.asObservable();

  private readonly isLoadedBS = new BehaviorSubject(false);
  public isLoaded$ = merge(
    this.isLoadedBS,
    // Timeout for showing the "loading" state
    timer(60 * 1000).pipe(map(() => true), shareReplay(1))
  );


  constructor(
    mapSettingsService: MapSettingsService,
    flagService: FlagService,
  ) {
    combineLatest([this.isVisible$, mapSettingsService.flagLayerFilter$, flagService.flags$]).pipe(
      takeUntil(this.ngUnsubscribe),
      map(([visible, flagFilter, flags]) => {
        // only override visibility when flag filtering is enabled
        if (!flagFilter.flagFilterEnabled) {
          return visible.layerVisible;
        }

        // flag layer must always be visible
        // TODO: Some kind of generic system to handle this instead of special casing flags, see also `HoverComponent.getTitle()`
        if (this.id === 'flagLayer') {
          return true;
        }

        // only show layers that contain flagged items
        return flags.some(flag => flag.layerId === this.id);
      }),
      distinctUntilChanged(),
    ).subscribe(this.shouldRenderBS);

    this.shouldRender$.pipe(
      takeUntil(this.ngUnsubscribe),
    ).subscribe(shouldRender => {
      if (this.mapRef?.getLayer(this.id)) {
        this.mapRef.setLayoutProperty(this.id, 'visibility', shouldRender ? 'visible' : 'none');
      }
      if (this.style) {
        this.style.visible = shouldRender;
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public toggleIsSelected() {
    this.setIsSelected(!this.isSelectedBS.getValue());

    // show group when the first layer within is shown
    if (
      this.getIsSelected() &&
      this.layerGroup &&
      !this.layerGroup.getIsVisible() &&
      this.layerGroup.layers.filter(layer => layer.getIsSelected()).length === 1
    ) {
      this.layerGroup.setVisibility(true);
    }
  }
  public setIsSelected(selected: boolean) {
    this.isSelectedBS.next(selected);
    const isGroupVisible = this.layerGroup?.getIsVisible();
    if (isGroupVisible) {
      this.setVisibility(selected);
    } else {
      this.setVisibility(false);
    }
  }
  public getIsSelected() {
    return this.isSelectedBS.getValue();
  }

  public toggleVisibility() {
    this.setVisibility(!this.isVisibleBS.getValue().layerVisible);
  }
  public setVisibility(isVisible: boolean, forceGroupVisibility = false) {
    if (this.layerGroup && forceGroupVisibility) {
      this.setIsSelected(isVisible);
      this.layerGroup.setVisibility(isVisible);
    } else {
      this.isVisibleBS.next({layerVisible: isVisible});
    }
  }

  public setLayerGroup(group: LayerGroup) {
    this.layerGroup = group;
  }

  public setLoadedStatus(isLoaded: boolean) {
    this.isLoadedBS.next(isLoaded);
  }
}
